import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	Divider,
	Grid,
	InputAdornment,
	Paper,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import PhoneIcon from "@mui/icons-material/Phone";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";

import { AvatarPersona } from "./AvatarPersona";
import { PersonaDocumentosPanel } from "./PersonaDocumentosPanel";
import {
	eliminarDocumentoPersona,
	descargarDocumentoPersona,
	getDetallePersona,
	getDocumentosPersona,
	normalizeHistorialFichajesPersonaItems,
	subirDocumentosPersona,
} from "../../services/apis";
import { api } from "../../services/apiClient";
import {
	canAccessRouteWithSnapshot,
	loadRouteAccessSnapshot,
} from "../../services/routeAccess";
import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { formatDateTimeEs, formatScoreOverHundred } from "../../utils/formatters";

const getEstadoColor = (estado) => {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
};

const getEstadoLabel = (estado) => {
	switch (estado) {
		case "pendiente":
			return "Pendiente";
		case "comprobado":
			return "Comprobado";
		case "validado":
			return "Validado";
		case "error":
			return "Error";
		default:
			return estado;
	}
};

const getInitialPersona = (statePersona, idUsuario) => {
	if (statePersona?.id) return statePersona;

	return {
		id: idUsuario,
		nombre: "",
		apellidos: "",
		telefono: "",
		nif: "",
		mail: "",
		puede_fichar: false,
		empresa: "",
		grupo: "",
		grupo_autofichaje: "",
		nombre_rol: "",
		imagen_url: null,
	};
};

const getPersonaDisplayName = (persona) =>
	[persona?.apellidos, persona?.nombre].filter(Boolean).join(" ").trim() ||
	persona?.nombre_completo ||
	"-";

const InfoField = ({ icon, label, value }) => (
	<Grid item size={{ xs: 12, sm: 6, lg: 4 }}>
		<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
				{icon}
				<Typography variant="subtitle2" color="text.secondary">
					{label}
				</Typography>
			</Box>
			<Typography variant="body1">{value || "-"}</Typography>
		</Paper>
	</Grid>
);

const getPersonaDocumentFallbackName = (documento) =>
	documento?.archivo_nombre || documento?.nombre || `documento-${documento?.id ?? "persona"}`;

const normalizeHistorialSearchValue = (value) =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const getAvatarHistoryImageKey = (item, index = 0) =>
	`avatar-${item?.id ?? "sin-id"}-${item?.fecha_desde ?? item?.fecha_evento ?? index}`;

const getHistorialItemTimestamp = (item) => {
	const fecha = item?.fecha_comprobacion || item?.fecha_cuadrilla || item?.fecha || item?.fecha_dia;
	const timestamp = Date.parse(String(fecha || ""));
	return Number.isFinite(timestamp) ? timestamp : 0;
};

const getLatestHistorialItem = (items = []) => {
	if (!Array.isArray(items) || items.length === 0) {
		return null;
	}

	return [...items].sort((left, right) => {
		const timestampDiff = getHistorialItemTimestamp(right) - getHistorialItemTimestamp(left);
		if (timestampDiff !== 0) {
			return timestampDiff;
		}

		return Number(right?.id_cuadrilla || 0) - Number(left?.id_cuadrilla || 0);
	})[0] || null;
};

export const DetallePersona = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const idUsuario = Number(id);
	const [tab, setTab] = useState("info");
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [persona, setPersona] = useState(() => getInitialPersona(location.state?.persona, idUsuario));
	const [historial, setHistorial] = useState([]);
	const [avatarHistory, setAvatarHistory] = useState([]);
	const [historialSearch, setHistorialSearch] = useState("");
	const [selectedImage, setSelectedImage] = useState(null);
	const [imageDataUrls, setImageDataUrls] = useState({});
	const [scopedPersonaViewer, setScopedPersonaViewer] = useState(false);
	const [canDeleteAvatarLuxand, setCanDeleteAvatarLuxand] = useState(false);
	const deferredHistorialSearch = useDeferredValue(historialSearch);

	const subtitulo = useMemo(() => {
		const partes = [persona?.nombre_rol, persona?.empresa].filter(Boolean);
		return partes.length > 0 ? partes.join(" Â· ") : "Sin rol o empresa";
	}, [persona?.empresa, persona?.nombre_rol]);

	const historialFiltrado = useMemo(() => {
		const searchTerm = normalizeHistorialSearchValue(deferredHistorialSearch);

		if (!searchTerm) {
			return historial;
		}

		return historial.filter((item) => {
			const searchableValue = normalizeHistorialSearchValue([
				item?.id_cuadrilla,
				item?.nombre_cuadrilla,
				item?.nombre_cliente,
				item?.fecha_comprobacion,
				item?.fecha_cuadrilla,
				item?.estado_comprobacion,
				getEstadoLabel(item?.estado_comprobacion || "pendiente"),
			].join(" "));

			return searchableValue.includes(searchTerm);
		});
	}, [deferredHistorialSearch, historial]);

	const ultimaParteTrabajada = useMemo(() => {
		return getLatestHistorialItem(historial);
	}, [historial]);

	const informacionLaboral = useMemo(() => {
		const grupoUltimoParte = ultimaParteTrabajada?.nombre_cuadrilla || "";

		return {
			empresa: ultimaParteTrabajada?.nombre_cliente || persona?.empresa || "",
			grupo: grupoUltimoParte || persona?.grupo || "",
			grupo_autofichaje: grupoUltimoParte || persona?.grupo_autofichaje || "",
		};
	}, [
		ultimaParteTrabajada?.nombre_cliente,
		ultimaParteTrabajada?.nombre_cuadrilla,
		persona?.empresa,
		persona?.grupo,
		persona?.grupo_autofichaje,
	]);

	const cargarPersonaDetalle = useCallback(async () => {
		setLoaded(false);
		setError("");

		if (!idUsuario) {
			setPersona(getInitialPersona(location.state?.persona, idUsuario));
			setError("No hay persona seleccionada");
			setLoaded(true);
			return;
		}

		try {
			const response = await getDetallePersona(idUsuario);

			if (response?.success !== true || !response?.data?.persona) {
				setError(response?.message || "No se pudo cargar la persona");
				return;
			}

			const historialInicial = normalizeHistorialFichajesPersonaItems(response?.data?.historial);
			const ultimaParteInicial = getLatestHistorialItem(historialInicial);

			setPersona((current) => ({
				...getInitialPersona(location.state?.persona, idUsuario),
				...current,
				...response.data.persona,
				empresa:
					ultimaParteInicial?.nombre_cliente ||
					response?.data?.persona?.empresa ||
					current?.empresa ||
					"",
				grupo:
					ultimaParteInicial?.nombre_cuadrilla ||
					response?.data?.persona?.grupo ||
					current?.grupo ||
					"",
				grupo_autofichaje:
					ultimaParteInicial?.nombre_cuadrilla ||
					response?.data?.persona?.grupo_autofichaje ||
					current?.grupo_autofichaje ||
					"",
			}));

			setHistorial(historialInicial);
			setAvatarHistory(
				Array.isArray(response?.data?.historial_avatares) ? response.data.historial_avatares : []
			);
		} catch (loadError) {
			console.error("Error cargando detalle de persona:", loadError);
			setError("No se pudo cargar la persona");
		} finally {
			setLoaded(true);
		}
	}, [idUsuario, location.state?.persona]);

	useEffect(() => {
		setPersona(getInitialPersona(location.state?.persona, idUsuario));
		setHistorial([]);
		setAvatarHistory([]);
		setHistorialSearch("");
		setSelectedImage(null);
		setImageDataUrls({});
	}, [idUsuario, location.state?.persona]);

	useEffect(() => {
		void cargarPersonaDetalle();
	}, [cargarPersonaDetalle]);

	useEffect(() => {
		let active = true;

		const cargarAccesoDetalle = async () => {
			try {
				const snapshot = await loadRouteAccessSnapshot();
				if (!active) {
					return;
				}

				const esVisorAcotado =
					canAccessRouteWithSnapshot("/cuadrillas", snapshot) &&
					!canAccessRouteWithSnapshot("/personas", snapshot);
				const esAdmin =
					Number(snapshot?.session?.rol || 0) === 986 ||
					canAccessRouteWithSnapshot("/admin", snapshot);

				setScopedPersonaViewer(esVisorAcotado);
				setCanDeleteAvatarLuxand(esAdmin);
			} catch (accessError) {
				console.error("Error cargando acceso del detalle de persona:", accessError);
				if (active) {
					setScopedPersonaViewer(false);
					setCanDeleteAvatarLuxand(false);
				}
			}
		};

		void cargarAccesoDetalle();

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (scopedPersonaViewer && tab === "docs") {
			setTab("info");
		}
	}, [scopedPersonaViewer, tab]);

	const fetchImageAsDataUrl = async (url, key) => {
		try {
			const response = await api.get(url, { responseType: 'blob' });
			const blob = response.data;
			const dataUrl = await new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = () => resolve(reader.result);
				reader.readAsDataURL(blob);
			});
			setImageDataUrls(prev => ({ ...prev, [key]: dataUrl }));
		} catch (error) {
			console.error('Error fetching image:', error);
		}
	};

	const getHistorialImageKey = useCallback((item) =>
		`${item?.id_usuario || persona?.id || "persona"}-${item?.id_cuadrilla || "parte"}-${item?.fecha_comprobacion || item?.fecha_cuadrilla || "fichaje"}`,
	[persona?.id]);

	useEffect(() => {
		historial.forEach((item) => {
			const imageKey = getHistorialImageKey(item);
			if (item.imagen_url && !imageDataUrls[imageKey]) {
				fetchImageAsDataUrl(item.imagen_url, imageKey);
			}
		});
	}, [getHistorialImageKey, historial, imageDataUrls]);

	useEffect(() => {
		avatarHistory.forEach((item, index) => {
			const imageKey = getAvatarHistoryImageKey(item, index);
			if (item?.imagen_url && !imageDataUrls[imageKey]) {
				fetchImageAsDataUrl(item.imagen_url, imageKey);
			}
		});
	}, [avatarHistory, imageDataUrls]);

	const handleAvatarUploaded = (updatedPersona) => {
		setPersona((current) => ({
			...current,
			...updatedPersona,
		}));
		void cargarPersonaDetalle();
	};

	const handleAvatarDeleted = (updatedPersona, result) => {
		setPersona((current) => ({
			...current,
			...updatedPersona,
		}));
		setAvatarHistory(
			Array.isArray(result?.data?.historial_avatares) ? result.data.historial_avatares : []
		);
	};

	const cargarDocumentosPersona = useCallback(async (personaActual) => {
		if (!personaActual?.id) {
			return {
				success: true,
				data: {
					documentos: [],
				},
				message: "La persona seleccionada no tiene identificador valido.",
			};
		}

		return getDocumentosPersona(personaActual.id);
	}, []);

	const subirDocumentosPersonaDesdePanel = useCallback(async (personaActual, files = [], options = {}) => {
		if (!personaActual?.id) {
			return {
				success: false,
				message: "La persona seleccionada no tiene identificador valido.",
			};
		}

		return subirDocumentosPersona(personaActual.id, files, options);
	}, []);

	const descargarDocumentoPersonaDesdePanel = useCallback(async (documento) => {
		if (!documento?.id) {
			throw new Error("Documento sin identificador valido.");
		}

		const { blob, fileName } = await descargarDocumentoPersona(documento.id);
		saveAs(blob, fileName || getPersonaDocumentFallbackName(documento));
	}, []);

	const eliminarDocumentoPersonaDesdePanel = useCallback(async (documento) => {
		if (!documento?.id) {
			return {
				success: false,
				message: "Documento sin identificador valido.",
			};
		}

		return eliminarDocumentoPersona(documento.id);
	}, []);

	const abrirParteDesdeHistorial = (item) => {
		const idCuadrilla = Number(item?.id_cuadrilla || 0);
		if (!idCuadrilla) return;

		navigate("/cuadrillas", {
			state: {
				cuadrillasReturnState: {
					reopenCuadrillaId: idCuadrilla,
				},
			},
		});
	};

	const abrirFotoHistorial = (event, item) => {
		event.stopPropagation();

		const imageKey = getHistorialImageKey(item);
		const imageDataUrl = imageDataUrls[imageKey];
		if (imageDataUrl) {
			setSelectedImage(imageDataUrl);
		}
	};

	const volver = () => {
		if (location.state?.from === "/cuadrillas" && location.state?.fromState) {
			navigate("/cuadrillas", {
				state: {
					cuadrillasReturnState: location.state.fromState,
				},
			});
			return;
		}

		if (typeof location.state?.from === "string" && location.state.from.trim()) {
			navigate(location.state.from);
			return;
		}

		if (scopedPersonaViewer) {
			navigate("/cuadrillas");
			return;
		}

		navigate("/personas");
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Detalle de persona" />
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 2 }}>
				<Button variant="text" startIcon={<ArrowBackIcon />} onClick={volver}>
					Volver
				</Button>
			</Grid>

			<Grid container size={{ xs: 12 }} sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined" sx={{ width: "100%", p: { xs: 2, md: 3 } }}>
					<Grid container spacing={3} alignItems="center">
						<Grid
							item
							size={{ xs: 12, md: 3 }}
							sx={{ display: "flex", justifyContent: "center" }}
						>
							<AvatarPersona
								persona={persona}
								size={140}
								allowUpload={!scopedPersonaViewer}
								allowDeleteLuxand={canDeleteAvatarLuxand}
								onAvatarUploaded={handleAvatarUploaded}
								onAvatarDeleted={handleAvatarDeleted}
							/>
						</Grid>

						<Grid item size={{ xs: 12, md: 9 }}>
							<Typography variant="h4" sx={{ mb: 0.5 }}>
								{getPersonaDisplayName(persona)}
							</Typography>

							<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
								{subtitulo}
							</Typography>

							<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
								<Chip label={`ID ${persona?.id || "-"}`} variant="outlined" />
								<Chip label={persona?.nif || "Sin NIF"} color="primary" variant="outlined" />
								{persona?.nombre_rol && <Chip label={persona.nombre_rol} />}
								{persona?.nombre_departamento && (
									<Chip label={persona.nombre_departamento} color="secondary" />
								)}
							</Box>

							{/* <Typography variant="body2" color="text.secondary">
								{tieneAvata
									? "Pulsa la foto para verla en grande o usa el icono de cÃ¡mara para actualizarla."
									: "Pulsa el avatar o el icono de cÃ¡mara para aÃ±adir una foto."}
							</Typography> */}
						</Grid>
					</Grid>

					<Divider sx={{ my: 3 }} />

					<Tabs
						value={tab}
						onChange={(_, value) => setTab(value)}
						variant="scrollable"
						allowScrollButtonsMobile
						sx={{ mb: 3 }}
					>
						<Tab label="Informacion" value="info" />
						<Tab label="Historial" value="historial" />
						{!scopedPersonaViewer && <Tab label="Documentacion" value="docs" />}
					</Tabs>

					{!loaded && (
						<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
							<CircularProgress />
						</Box>
					)}

					{loaded && error && <Alert severity="error">{error}</Alert>}

					{loaded && !error && tab === "info" && (
						<Grid container spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2 }}>Datos personales</Typography>
							</Grid>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="ID"
								value={persona?.id || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="Nombre"
								value={persona?.nombre || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="Apellidos"
								value={persona?.apellidos || "-"}
							/>
							<InfoField
								icon={<PhoneIcon color="primary" fontSize="small" />}
								label="Teléfono"
								value={persona?.telefono || "-"}
							/>
							<InfoField
								icon={<BadgeIcon color="primary" fontSize="small" />}
								label="NIF"
								value={persona?.nif || "-"}
							/>

							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Permisos y accesos</Typography>
							</Grid>
							<InfoField
								icon={<AlternateEmailIcon color="primary" fontSize="small" />}
								label="Email"
								value={persona?.mail || "-"}
							/>
							<InfoField
								icon={<AssignmentIndIcon color="primary" fontSize="small" />}
								label="Puede fichar"
								value={persona?.puede_fichar ? "SÃ­" : "No"}
							/>

							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Información laboral</Typography>
							</Grid>
							<InfoField
								icon={<BusinessIcon color="primary" fontSize="small" />}
								label="Empresa"
								value={informacionLaboral?.empresa || "-"}
							/>
							<InfoField
								icon={<GroupWorkIcon color="primary" fontSize="small" />}
								label="Grupo"
								value={informacionLaboral?.grupo || "-"}
							/>
							<InfoField
								icon={<GroupWorkIcon color="primary" fontSize="small" />}
								label="Grupo autofichaje"
								value={informacionLaboral?.grupo_autofichaje || "-"}
							/>
							<InfoField
								icon={<AssignmentIndIcon color="primary" fontSize="small" />}
								label="Rol"
								value={persona?.nombre_rol || "-"}
							/>

							<Grid item size={{ xs: 12 }}>
								<Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
									Historial de avatares
								</Typography>
							</Grid>

							{avatarHistory.length === 0 ? (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="info">
										Esta persona no tiene historial de avatares visible.
									</Alert>
								</Grid>
							) : (
								avatarHistory.map((avatarItem, index) => {
									const imageKey = getAvatarHistoryImageKey(avatarItem, index);
									const previewSrc = imageDataUrls[imageKey] || "";

									return (
										<Grid item size={{ xs: 12 }} key={imageKey}>
											<Paper variant="outlined" sx={{ p: 2 }}>
												<Grid container spacing={2} alignItems="center">
													<Grid item size={{ xs: 12, md: 2 }}>
														{previewSrc ? (
															<Box
																component="img"
																src={previewSrc}
																alt={`Avatar ${index + 1}`}
																onClick={() => setSelectedImage(previewSrc)}
																sx={{
																	width: 88,
																	height: 88,
																	objectFit: "cover",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	cursor: "pointer",
																}}
															/>
														) : (
															<Box
																sx={{
																	width: 88,
																	height: 88,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	backgroundColor: "grey.100",
																}}
															>
																{avatarItem?.imagen_url ? <CircularProgress size={22} /> : "Sin foto"}
															</Box>
														)}
													</Grid>

													<Grid item size={{ xs: 12, md: 6 }}>
														<Typography variant="subtitle1" sx={{ mb: 0.75 }}>
															{avatarItem?.es_actual ? "Avatar actual" : `Avatar ${index + 1}`}
														</Typography>
														<Typography variant="body2" color="text.secondary">
															Desde: {formatDateTimeEs(avatarItem?.fecha_desde || avatarItem?.fecha_evento) || "-"}
														</Typography>
														<Typography variant="body2" color="text.secondary">
															Hasta: {formatDateTimeEs(avatarItem?.fecha_hasta) || (avatarItem?.es_actual ? "Actual" : "-")}
														</Typography>
													</Grid>

													<Grid item size={{ xs: 12, md: 4 }}>
														<Box
															sx={{
																display: "flex",
																gap: 1,
																flexWrap: "wrap",
																justifyContent: { xs: "start", md: "end" },
															}}
														>
															<Chip
																size="small"
																color={avatarItem?.es_actual ? "success" : "default"}
																label={avatarItem?.es_actual ? "Actual" : "Historico"}
															/>
															{avatarItem?.eliminado && (
																<Chip size="small" color="warning" label="Desactivado" />
															)}
															<Chip
																size="small"
																variant="outlined"
																label={
																	avatarItem?.puede_eliminar_luxand
																		? "Vinculado "
																		: "Sin vinculo"
																}
															/>
														</Box>
													</Grid>
												</Grid>
											</Paper>
										</Grid>
									);
								})
							)}
						</Grid>
					)}

					{loaded && !error && tab === "historial" && (
						<Grid container direction="column" spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<TextField
									fullWidth
									size="small"
									label="Buscar en historial"
									placeholder="Parte, cliente, fecha, estado o ID"
									value={historialSearch}
									onChange={(event) => setHistorialSearch(event.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>

							{historial.length === 0 ? (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="info">
										Esta persona no tiene historial de fichajes visible.
									</Alert>
								</Grid>
							) : historialFiltrado.length === 0 ? (
								<Grid item size={{ xs: 12 }}>
									<Alert severity="info">
										No hay fichajes que coincidan con la busqueda actual.
									</Alert>
								</Grid>
							) : (
								historialFiltrado.map((item, index) => {
									const imageKey = getHistorialImageKey(item);
									const imageDataUrl = imageDataUrls[imageKey];

									return (
									<Grid item size={{ xs: 12 }} key={`${item.id_cuadrilla}-${index}`}>
										<Paper
											variant="outlined"
											onClick={() => abrirParteDesdeHistorial(item)}
											sx={{
												p: 2,
												cursor: item.id_cuadrilla ? "pointer" : "default",
												transition: "border-color 0.2s, box-shadow 0.2s",
												"&:hover": item.id_cuadrilla
													? {
														borderColor: "primary.main",
														boxShadow: 1,
													}
													: undefined,
											}}
										>
											<Grid container spacing={2} alignItems="center">
												{item.imagen_url && (
													<Grid item size={{ xs: 12, md: 2 }}>
														{imageDataUrl ? (
															<Box
																component="img"
																src={imageDataUrl}
																alt="Imagen de fichaje"
																onClick={(event) => {
																	event.stopPropagation();
																	setSelectedImage(imageDataUrl);
																}}
																sx={{
																	width: 80,
																	height: 80,
																	objectFit: "cover",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	cursor: "pointer",
																	transition: "transform 0.2s",
																	"&:hover": {
																		transform: "scale(1.05)",
																	},
																}}
															/>
														) : (
															<Box
																sx={{
																	width: 80,
																	height: 80,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	borderRadius: 1,
																	border: "1px solid",
																	borderColor: "divider",
																	backgroundColor: "grey.100",
																}}
															>
																<CircularProgress size={24} />
															</Box>
														)}
													</Grid>
												)}

												<Grid item size={{ xs: 12, md: item.imagen_url ? 4 : 6 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
														<GroupWorkIcon color="warning" fontSize="small" />
														<Typography variant="subtitle1">
															{item.nombre_cuadrilla || `Parte ${item.id_cuadrilla}`}
														</Typography>
													</Box>
												<Typography variant="body2" color="text.secondary">
													Cliente: {item.nombre_cliente || "-"}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													Cabo: {[item.apellidos_cabo, item.nombre_cabo].filter(Boolean).join(" ") || "-"}
												</Typography>
											</Grid>

												<Grid item size={{ xs: 12, md: item.imagen_url ? 3 : 3 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
														<EventIcon color="primary" fontSize="small" />
														<Typography variant="body2">
															{formatDateTimeEs(item.fecha_comprobacion || item.fecha_cuadrilla)}
														</Typography>
													</Box>
												</Grid>

												<Grid item size={{ xs: 12, md: item.imagen_url ? 3 : 3 }}>
													<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "start", md: "end" } }}>
														<Chip
															label={getEstadoLabel(item.estado_comprobacion || "pendiente")}
															color={getEstadoColor(item.estado_comprobacion)}
															size="small"
														/>
														{item.puntuacion_comprobacion && (
															<Chip
																label={`Similitud: ${formatScoreOverHundred(item.puntuacion_comprobacion)}`}
																size="small"
																variant="outlined"
															/>
														)}
														{item.imagen_url && (
															<Chip
																label={imageDataUrl ? "Ver foto" : "Cargando foto"}
																size="small"
																variant="outlined"
																color="primary"
																onClick={(event) => abrirFotoHistorial(event, item)}
																disabled={!imageDataUrl}
															/>
														)}
													</Box>
												</Grid>
											</Grid>
										</Paper>
									</Grid>
									);
								})
							)}

						</Grid>
					)}

					{loaded && !error && tab === "docs" && (
						<Grid container spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Alert severity="info">
									Los documentos de persona se almacenan en una carpeta privada,
									se descargan mediante endpoint autenticado y ahora se organizan
									por campaña y carpeta documental.
								</Alert>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
										<InsertDriveFileIcon color="action" />
										<Typography variant="subtitle2">Documentacion asociada</Typography>
									</Box>
									<PersonaDocumentosPanel
										persona={persona}
										expanded={tab === "docs"}
										loadDocuments={cargarDocumentosPersona}
										allowDropzone
										uploadDocuments={subirDocumentosPersonaDesdePanel}
										downloadDocument={descargarDocumentoPersonaDesdePanel}
										deleteDocument={eliminarDocumentoPersonaDesdePanel}
									/>
								</Paper>
							</Grid>
						</Grid>
					)}
				</Paper>
			</Grid>

			<Dialog
				open={!!selectedImage}
				onClose={() => setSelectedImage(null)}
				maxWidth="md"
				fullWidth
			>
				<DialogContent sx={{ p: 1, display: "flex", justifyContent: "center" }}>
					{selectedImage && (
						<Box
							component="img"
							src={selectedImage}
							alt="Imagen ampliada de fichaje"
							sx={{
								maxWidth: "100%",
								maxHeight: "80vh",
								objectFit: "contain",
							}}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Grid>
	);
};
